import z from 'zod';
// Imported for the side effect: it is where zod gains `.openapi`.
import './schemas';
import { createTransformer } from './utils';

/**
 * `bytea` as the driver returns it. Typed as `Buffer` but checked as
 * `Uint8Array`, which `Buffer` extends.
 */
export const DBSha256Schema = z.custom<Buffer>(
  value => value instanceof Uint8Array && value.length === 32,
  'A SHA-256 digest is 32 bytes'
);

export const Sha256Schema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, 'Must be a lowercase hex SHA-256 digest')
  .openapi({
    description: 'SHA-256 digest, lowercase hex',
    example: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
  });

export const DBTimestampSchema = z.date();

/** The driver returns `bigint` as a string, so no precision is lost. */
export const DBBigIntSchema = z.union([z.string(), z.number(), z.bigint()]);

/** The driver returns `numeric` as a string too. */
export const DBNumericSchema = z.union([z.string(), z.number()]);

/** A fraction of the page's width or height, so a crop still fits at another render size. */
export const PageFractionSchema = z.number().min(0).max(1).openapi({
  description: 'Position as a fraction of the page, 0 to 1',
  example: 0.32,
});

/** `jsonb` holding an object the schema deliberately does not describe. */
export const DBJsonObjectSchema = z.record(z.string(), z.unknown());
export const JsonObjectSchema = z.record(z.string(), z.unknown());

type AnyObject = z.ZodObject<any>;

// Pass `nullable` for a nullable column; without it a null timestamp or numeric
// decodes to 0 and a null digest throws.

/** A `bytea` digest: lowercase hex for the API, bytes for the driver. */
export const createSha256Transformer = <TClient extends AnyObject, TDB extends AnyObject>(
  field: string,
  { nullable = false }: { nullable?: boolean } = {}
) =>
  createTransformer<TClient, TDB>({
    encoder: {
      sourceFields: [field] as never,
      encode: value => {
        const hex = (value as Record<string, string | null>)[field];
        if (nullable && (hex === null || hex === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: Buffer.from(hex as string, 'hex') } as never;
      },
    },
    decoder: {
      sourceFields: [field] as never,
      decode: value => {
        const bytes = (value as Record<string, Buffer | null>)[field];
        if (nullable && (bytes === null || bytes === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: Buffer.from(bytes as Buffer).toString('hex') } as never;
      },
    },
  });

/** A `timestamptz`: unix milliseconds for the API, a `Date` for the driver. */
export const createTimestampTransformer = <TClient extends AnyObject, TDB extends AnyObject>(
  field: string,
  { nullable = false }: { nullable?: boolean } = {}
) =>
  createTransformer<TClient, TDB>({
    encoder: {
      sourceFields: [field] as never,
      encode: value => {
        const millis = (value as Record<string, number | null>)[field];
        if (nullable && (millis === null || millis === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: new Date(millis as number) } as never;
      },
    },
    decoder: {
      sourceFields: [field] as never,
      decode: value => {
        const at = (value as Record<string, Date | null>)[field];
        if (nullable && (at === null || at === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: new Date(at as Date).getTime() } as never;
      },
    },
  });

/**
 * A `bigint` or `numeric`: a number for the API. The driver returns a string
 * because these types cannot always be held exactly in a JavaScript number; the
 * columns that use this transformer hold values that can.
 */
export const createNumericTransformer = <TClient extends AnyObject, TDB extends AnyObject>(
  field: string,
  { nullable = false }: { nullable?: boolean } = {}
) =>
  createTransformer<TClient, TDB>({
    encoder: {
      sourceFields: [field] as never,
      encode: value => {
        const amount = (value as Record<string, number | null>)[field];
        if (nullable && (amount === null || amount === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: amount } as never;
      },
    },
    decoder: {
      sourceFields: [field] as never,
      decode: value => {
        const amount = (value as Record<string, string | number | null>)[field];
        if (nullable && (amount === null || amount === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: Number(amount) } as never;
      },
    },
  });
